import { makeVar } from "@apollo/client";
import { useReactiveVar } from "@apollo/client/react";
import React, { useCallback, useState } from "react";

import { reportErrorToNR } from "../util/reportErrorToNR";

export enum ErrorSeverity {
  HIGH = "high",
  MEDIUM = "medium",
  LOW = "low",
}

export type ErrorReporter = (severity: ErrorSeverity, message: string, errorMeta?: Record<string, unknown>) => void;
type ErrorState = { message: string; severity: ErrorSeverity; meta?: Record<string, string> };

const errorVar = makeVar<ErrorState | undefined>(undefined);

const safeStringify = (value: unknown): string => {
  const seen = new WeakSet<object>();
  try {
    return JSON.stringify(value, (_, next) => {
      if (typeof next === "object" && next !== null) {
        if (seen.has(next)) {
          return "[Circular]";
        }
        seen.add(next);
      }

      if (next instanceof HTMLElement) {
        return `[HTMLElement ${next.tagName}]`;
      }

      return next;
    });
  } catch {
    return "[Unserializable]";
  }
};

const simplifyErrorMeta = (errorMeta?: Record<string, unknown>): Record<string, string> | undefined => {
  if (!errorMeta) {
    return undefined;
  }

  return Object.keys(errorMeta).reduce(
    (acc, key) => {
      if (typeof errorMeta[key] === "object") {
        acc[key] = safeStringify(errorMeta[key]);
      } else {
        acc[key] = String(errorMeta[key]);
      }
      return acc;
    },
    {} as Record<string, string>,
  );
};

export const useErrorHandler = (severity?: ErrorSeverity) => {
  const error = useReactiveVar(errorVar);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Set initial loading to false after a short delay to allow components to stabilize
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
    }, 1000); // 1 second delay to allow initial loading to complete

    return () => clearTimeout(timer);
  }, []);

  const throwError = useCallback(
    async (error: string | Error, errorMeta: Record<string, unknown> = {}): Promise<void> => {
      reportErrorToNR(error, simplifyErrorMeta(errorMeta));
    },
    [],
  );

  const reportError = useCallback(
    async (severity: ErrorSeverity, message: string, errorMeta: Record<string, unknown> = {}): Promise<void> => {
      const simplifiedErrorMeta = simplifyErrorMeta(errorMeta);

      throwError(message, errorMeta);

      // Only set error state if not in initial loading phase or if it's a critical error
      if (!isInitialLoading || severity === ErrorSeverity.HIGH) {
        errorVar({ message, severity, meta: simplifiedErrorMeta });
      }
    },
    [throwError, isInitialLoading],
  );

  return {
    reportError,
    error: error?.severity === severity ? error?.message : undefined,
    errorMeta: error?.severity === severity ? error?.meta : undefined,
    throwError,
  };
};
